import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { BulkActionToolbar, CorrectionsModal, FullTable, ToastScreen } from 'components';
import {
  ACTION_TYPE,
  ACTION_TYPE_CREATE_BATCH,
  PAYABLE,
  payablesBody,
  payablesHead,
  SEND_CORRECTION,
} from './constants';
import { LoadDetailSwitcher } from '../../loads';
import { AddPayoutDate } from './addPayoutDate';
import { MoveTo } from './moveTo';
import { FindError, FindLoad, FindSuccess, useModal, usePrimaryModal } from 'utils';
import {
  factoringActions,
  httpRequestsOnErrorsActions,
  httpRequestsOnLoadActions,
  httpRequestsOnSuccessActions,
  loadActions,
  LoadServices,
} from 'store';
import { LoadService } from '../../../fragments/loads/load.service';
import { PayableNotes } from './payableNotes';

export const OpenPayables = ({ getTotals }) => {
  const [selectedLoads, setSelectedLoads] = useState([]);
  const [showBulkAction, setShowBulkAction] = useState(false);
  const [correction, setCorrection] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();
  const intervalRef = useRef(null);
  const { loadsList, factoringsList } = useSelector((state) => ({
    loadsList: state.loads.loadsList,
    factoringsList: state.factorings.factoringsList,
  }));
  const info = history?.location?.state;
  const successCreateBatch = FindSuccess(ACTION_TYPE_CREATE_BATCH);
  const successCreatePayableNote = FindSuccess('CREATE_PAYABLE_NOTE');
  const successEditPayableNote = FindSuccess('EDIT_PAYABLE_NOTE');
  const successDeletePayableNote = FindSuccess('DELETE_PAYABLE_NOTE');
  const { openPrimary, closePrimary, params } = usePrimaryModal();
  const { open, close } = useModal({ noPadding: true, noCloseButton: true });
  const correctionsLoading = FindLoad(SEND_CORRECTION);
  const beckErrorSendCorrection = FindError(SEND_CORRECTION);
  const beckErrorEditPayablesTable = FindError('EDIT_OPEN_PAYABLES_TABLE');
  const totalFinalAmount = selectedLoads?.reduce((sum, load) => sum + LoadService.getPayableBalance(load?.payable), 0);

  useEffect(() => {
    if (beckErrorSendCorrection?.[0]?.error) {
      dispatch(httpRequestsOnErrorsActions.removeError(SEND_CORRECTION));

      toast(
        <ToastScreen
          type={'error'}
          text={'This action isn’t available right now for this load.'}
        />,
      );
    }

  }, [beckErrorSendCorrection?.[0]?.error]);


  useEffect(() => {
    if (successCreatePayableNote?.length || successEditPayableNote?.length || successDeletePayableNote?.length) {

      if (successCreatePayableNote?.length) {
        dispatch(httpRequestsOnSuccessActions.removeSuccess('CREATE_PAYABLE_NOTE'));
      }

      if (successEditPayableNote?.length) {
        dispatch(httpRequestsOnSuccessActions.removeSuccess('EDIT_PAYABLE_NOTE'));
      }

      if (successDeletePayableNote?.length) {
        dispatch(httpRequestsOnSuccessActions.removeSuccess('DELETE_PAYABLE_NOTE'));
      }

      handleGet('noLoad');
      close();
    }
  }, [successCreatePayableNote, successEditPayableNote, successDeletePayableNote]);


  useEffect(() => {
    if (beckErrorEditPayablesTable?.[0]?.error) {
      dispatch(httpRequestsOnErrorsActions.removeError('EDIT_OPEN_PAYABLES_TABLE'));

      toast(
        <ToastScreen
          type={'error'}
          text={beckErrorEditPayablesTable?.[0]?.error}
        />,
      );
    }

  }, [beckErrorEditPayablesTable?.[0]?.error]);

  useEffect(() => {
    if (successCreateBatch?.length) {
      dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE_CREATE_BATCH));
      closePrimary();
      setSelectedLoads([]);
      setShowBulkAction(false);
      handleGet('noLoad');
      getTotals()
    }
  }, [successCreateBatch]);

  useEffect(() => {
    dispatch(factoringActions.getFactorings({ status: 'ACTIVE' }));
  }, []);

  useEffect(() => {
    handleGet('loading');

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      handleGet('noLoad');
    }, 30000);
    return () => clearInterval(intervalRef.current);
  }, [info]);


  useEffect(() => {
    const isEmpty = Object.keys(params).length === 0;

    setShowBulkAction(isEmpty);
  }, [params]);


  const renderParams = () => {
    const filteredInfo = {
      ...info,
    };
    filteredInfo.skip = info?.skip ? info?.skip : 0;
    filteredInfo.limit = 200;

    filteredInfo?.mcs ? filteredInfo.mcs = filteredInfo.mcs?.map((mc) => mc?.id) : delete filteredInfo?.mcs;
    filteredInfo?.offices ? filteredInfo.offices = filteredInfo.offices?.map((office) => office?.id) : delete filteredInfo?.offices;

    delete filteredInfo.page;
    delete filteredInfo.activeTab;
    delete filteredInfo.viewType;
    return filteredInfo;
  };

  const handleGet = (loading) => {
    const loader = loading === 'loading' ? 'loading' : 'noLoad';
    const filteredInfo = renderParams();
    dispatch(loadActions.getLoadsByStatus({ ...filteredInfo, department: PAYABLE }, loader));
  };

  const handleSelect = (e, item) => {
    setSelectedLoads((prevItems) => {
      const exists = prevItems.some((i) => i.id === item.id);
      if (exists) {
        return prevItems.filter((i) => i.id !== item.id);
      } else {
        return [...prevItems, item];
      }
    });
  };

  const handleSelectAll = (e, selectAll) => {
    if (e.target.checked || selectAll) {
      const allIds = loadsList?.loads?.map(load => load);
      setSelectedLoads(allIds);
    } else {
      setSelectedLoads([]);
    }
  };


  const handleSend = async (id) => {
    dispatch(httpRequestsOnLoadActions.appendLoading(SEND_CORRECTION));
    try {
      const date = { 'correction': correction };
      await LoadServices.PayableCorrections(id, date);
      handleGet();
      getTotals();
      closePrimary();
    } finally {
      dispatch(httpRequestsOnLoadActions.removeLoading(SEND_CORRECTION));
    }
  };

  const handleClick = (item) => {
    openPrimary(
      <CorrectionsModal
        handleChangeText={(value) => setCorrection(value)}
        loadingCor={correctionsLoading?.length}
        actionType={SEND_CORRECTION}
        handleSend={() => handleSend(item?.id)}
        type={'notVerified'}
        handleClose={closePrimary}
      />);
  };

  const handleAddNote = (item) => {
    open(
      <PayableNotes
        load={item}
        afterSuccess={() => {
          handleGet('noLoad');
        }}
      />);
  };

  const changeRowItem = (value, itemId, type) => {
    const body = {};
    if (type === 'payee') body.payeeId = value?.id;
    if (type === 'term') body.term = value?.id;
    if (type === 'method') body.method = value?.id;

    const updatedList = loadsList?.loads.map(load => {
      if (type === 'payee' && load?.id === itemId) {
        return {
          ...load,
          payable: {
            ...load?.payable,
            payeeId: value?.id,
            payeeName: value?.name,
          },
        };
      }

      if (type === 'term' && load?.id === itemId) {
        return {
          ...load,
          payable: {
            ...load?.payable,
            pmtTerm: value?.id,
          },
        };
      }

      if (type === 'method' && load?.id === itemId) {
        return {
          ...load,
          payable: {
            ...load?.payable,
            method: value?.id,
          },
        };
      }

      return load;
    });


    dispatch(loadActions.editLoadList({ loads: updatedList, count: loadsList?.count }));
    dispatch(loadActions.editOpenPayablesTable(itemId, body, type, { ...renderParams(), department: PAYABLE }));
  };

  return (
    <div>
      <FullTable
        head={payablesHead({ handleSelectAll, list: loadsList?.loads, currentList: selectedLoads })}
        body={payablesBody({
          handleSelect,
          currentList: selectedLoads,
          clickRequestCorrection: handleClick,
          factoringsList: factoringsList?.factorings,
          changeRowItem,
          handleAddNote,
          afterSuccess: () => {
            handleGet('noLoad');
          },
        })}
        limitCount={200}
        loadingType={ACTION_TYPE}
        list={loadsList?.loads}
        listCount={loadsList?.count}
        fixHeight={loadsList?.count ? '65vh' : '72vh'}
        handleClick={(id) =>
          openPrimary(<LoadDetailSwitcher loadId={id} />)
        }
        noText={'Open Payables'}
        padding={'8px'}
        paddingHead={'8px'}
      />

      {(!!selectedLoads?.length && showBulkAction) &&
        <BulkActionToolbar
          selectedCount={selectedLoads?.length}
          allAmount={totalFinalAmount}
          handleSelectOrUnselectAll={handleSelectAll}
          handleCreateBatch={() => openPrimary(
            <AddPayoutDate
              selectedLoads={selectedLoads}
              handleClose={closePrimary}
            />)}
          handleMoveTo={() => openPrimary(
            <MoveTo
              selectedLoads={selectedLoads}
              handleClose={closePrimary}
              afterSuccess={() => {
                handleGet('noLoad');
                setSelectedLoads([]);
                getTotals()
              }}
            />,
          )
          }
          isAllSelected={selectedLoads?.length === loadsList?.loads?.length}
          handleClose={() => setSelectedLoads([])}
        />
      }
    </div>
  );
};

